/* eslint-disable max-classes-per-file */
import {
  ViewContainerRef,
  Directive,
  ElementRef,
  Input,
  Renderer2,
  Injector,
  ChangeDetectorRef,
  ComponentRef,
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  effect,
} from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';

import { SvgName } from '../app.config';

export type ButtonDirectiveColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'default'
  | 'danger'
  | 'warning'
  | 'transparent'
  | 'alpha'
  | 'surface'
  | 'custom';

@Directive({
  selector: 'a[appButton], button[appButton]',
  standalone: true,
})
export class ButtonDirective {
  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly injector = inject(Injector);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly loading = input<boolean>(false);
  readonly shape = input<'circle' | 'outlined' | 'rounded' | 'square'>('circle');
  readonly color = input<ButtonDirectiveColor>('primary');
  readonly icon = input<SvgName | undefined>(undefined);
  readonly iconPosition = input<'left' | 'right' | 'only-icon'>('left');
  readonly size = input<'sm' | 'md' | 'lg'>('lg');
  readonly mutableContent = input<string | undefined>(undefined); // We may want to replace this with MutationObserver

  divContainerElement!: HTMLDivElement;
  divTextElement!: HTMLDivElement;
  svgElement!: ComponentRef<BtnIconComponent>;
  loadingElement!: ComponentRef<BtnIconComponent>;

  _buttonContent?: string | undefined;

  constructor() {
    effect(() => {
      // This fix the BtnIconComponent instantiation error
      this.cdr.detach();

      // reset state classes
      this.removeClasses(
        this.el.nativeElement,
        // eslint-disable-next-line max-len
        'left-icon right-icon icon no-icon left right only-icon circle outlined rounded square primary secondary tertiary default transparent alpha danger warning custom surface',
      );

      const getButtonContent = () => {
        if (!this._buttonContent) {
          this._buttonContent = this.el.nativeElement.innerHTML;
        }

        const mutableContent = this.mutableContent();
        if (mutableContent) {
          return mutableContent;
        }
        return this._buttonContent as string;
      };
      const buttonContent = getButtonContent();

      this.renderer.setProperty(this.el.nativeElement, 'innerHTML', '');

      this.divContainerElement = this.renderer.createElement('div');
      this.renderer.appendChild(this.el.nativeElement, this.divContainerElement);

      this.divTextElement = this.renderer.createElement('div');
      this.renderer.appendChild(this.divTextElement, this.renderer.createText(buttonContent));

      if (!this.icon()) {
        this.renderer.appendChild(this.divContainerElement, this.divTextElement);
      } else {
        const svgIcon = this.viewContainerRef.createComponent(BtnIconComponent, { injector: this.injector });
        svgIcon.instance.key = this.icon() as SvgName;
        svgIcon.instance.size = this.size();

        switch (this.iconPosition()) {
          case 'left': {
            this.renderer.appendChild(this.divContainerElement, svgIcon.location.nativeElement);
            this.addClasses(svgIcon.location.nativeElement, 'left-icon');
            this.renderer.appendChild(this.divContainerElement, this.divTextElement);
            break;
          }
          case 'right': {
            this.renderer.appendChild(this.divContainerElement, this.divTextElement);
            this.renderer.appendChild(this.divContainerElement, svgIcon.location.nativeElement);
            this.addClasses(svgIcon.location.nativeElement, 'right-icon');
            break;
          }
          case 'only-icon': {
            this.renderer.appendChild(this.divContainerElement, svgIcon.location.nativeElement);
            break;
          }
        }
      }

      if (this.icon()) {
        this.addClasses(this.el.nativeElement, 'icon');
      } else {
        this.addClasses(this.el.nativeElement, 'no-icon');
      }
      if (this.iconPosition()) {
        this.addClasses(this.el.nativeElement, this.iconPosition());
      }

      this.addClasses(this.divContainerElement, 'wrapper-container');
      this.addClasses(this.divTextElement, 'text-container');

      this.addClasses(this.el.nativeElement, this.shape());
      this.addClasses(this.el.nativeElement, this.color());
      this.addClasses(this.el.nativeElement, this.size());

      this.setLoading(this.loading());
      this.cdr.reattach();
      this.cdr.detectChanges();
    });
  }

  private addClasses(el: HTMLElement, classes: string) {
    classes.split(' ').forEach((c) => this.renderer.addClass(el, c));
  }

  private removeClasses(el: HTMLElement, classes: string) {
    classes.split(' ').forEach((c) => this.renderer.removeClass(el, c));
  }

  private setLoading(loading: boolean) {
    if (loading) {
      this.addClasses(this.el.nativeElement, 'loading');
      if (!this.loadingElement) {
        this.loadingElement = this.viewContainerRef.createComponent(BtnIconComponent, { injector: this.injector });
        this.loadingElement.instance.key = 'progress';
      }
      if (!this.icon()) {
        this.renderer.appendChild(this.divContainerElement, this.loadingElement.location.nativeElement);
      } else {
        switch (this.iconPosition()) {
          case 'left': {
            this.renderer.appendChild(this.divContainerElement, this.loadingElement.location.nativeElement);
            break;
          }
          case 'right': {
            this.renderer.insertBefore(
              this.divContainerElement,
              this.loadingElement.location.nativeElement,
              this.divTextElement,
              false,
            );
            break;
          }
          case 'only-icon': {
            break;
          }
        }
      }
      this.addClasses(this.loadingElement.location.nativeElement, 'loading-icon');
    } else {
      this.removeClasses(this.el.nativeElement, 'loading');
      if (this.loadingElement) {
        this.renderer.removeChild(this.divContainerElement, this.loadingElement.location.nativeElement);
      }
    }
  }
}

/**
 * FIXME: The dynamic instantiation of an svg-icon component is problematic.
 * This is a simple svg-icon wrapper and is a temporary workaround.
 */
@Component({
  selector: 'app-button-icon',
  standalone: true,
  imports: [SvgIconComponent],
  template: '<svg-icon [size]="size" [key]="key"></svg-icon>',
  styles: [
    `
      :host {
        @apply flex items-center justify-center;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BtnIconComponent {
  @Input({ required: true }) key!: SvgName;
  @Input({ required: true }) size!: 'sm' | 'md' | 'lg';
}
